import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { Plus, Trash2, Loader2, Handshake, Target, RefreshCcw, LogOut, FileText, Mail, X, FolderOpen, Check, Clock, Map, Sparkles, AlertTriangle, BellRing } from "lucide-react";
import { toast } from "sonner";
import { InvestorMapPanel } from "../components/studio/InvestorMapPanel";
import { StrategyPanel } from "../components/studio/StrategyPanel";

const INTRO_STATUSES = ["intro_made", "meeting", "term_sheet", "closed", "passed"];
const STATUS_LABEL = { intro_made: "Intro made", meeting: "Meeting", term_sheet: "Term sheet", closed: "Closed", passed: "Passed" };

export default function StudioDashboard() {
  const { user, logout } = useAuth();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [kpiDraft, setKpiDraft] = useState({});
  const [introDraft, setIntroDraft] = useState({});
  const [invites, setInvites] = useState([]);
  const [inviteOffer, setInviteOffer] = useState(null);
  const [closeoutBusy, setCloseoutBusy] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/studio/portfolio");
      setRows(data.companies || []);
    } catch (_e) {} finally { setLoading(false); }
  };
  const loadInvites = async () => {
    try {
      const { data } = await api.get("/studio/invites");
      setInvites(data.invites || []);
    } catch (_e) {}
  };
  useEffect(() => { load(); loadInvites(); }, []);

  const addCompany = async (e) => {
    e.preventDefault();
    setBusy(true); setInviteOffer(null);
    try {
      await api.post("/studio/portfolio", { founder_email: email });
      setEmail(""); toast.success("Company added to your portfolio"); load();
    } catch (err) {
      const detail = err?.response?.data?.detail || "Could not add company";
      if (err?.response?.status === 404 && detail.includes("sign up")) setInviteOffer(email);
      else toast.error(detail);
    } finally { setBusy(false); }
  };

  const sendInvite = async (em) => {
    setBusy(true);
    try {
      const { data } = await api.post("/studio/invites", { founder_email: em });
      if (data.linked) { toast.success("They're already on Clinq — added to your portfolio"); load(); }
      else toast.success(`Invite sent to ${em}`);
      setInviteOffer(null); setEmail(""); loadInvites();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not send invite");
    } finally { setBusy(false); }
  };
  const revokeInvite = async (id) => {
    try { await api.delete(`/studio/invites/${id}`); loadInvites(); } catch (_e) {}
  };

  const downloadCloseout = async () => {
    setCloseoutBusy(true);
    try {
      const res = await api.get("/studio/report/closeout.pdf", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = "clinq-engagement-closeout-report.pdf"; a.click();
      URL.revokeObjectURL(url);
    } catch (_e) { toast.error("Could not generate the close-out report"); }
    finally { setCloseoutBusy(false); }
  };

  const downloadReport = async (cid, name) => {
    try {
      const res = await api.get(`/studio/portfolio/${cid}/report.pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = `${(name || "company").toLowerCase().replace(/\s+/g, "-")}-ccr-report.pdf`; a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(e?.response?.status === 404 ? "No CCR analysis yet for this company" : "Could not download the report");
    }
  };

  const [expanded, setExpanded] = useState({}); // {companyId: "map" | "strategy" | null}
  const togglePanel = (cid, panel) =>
    setExpanded((s) => ({ ...s, [cid]: s[cid] === panel ? null : panel }));

  const [nudging, setNudging] = useState(null);
  const nudgeFounder = async (cid) => {
    setNudging(cid);
    try {
      const { data } = await api.post(`/studio/portfolio/${cid}/doc-nudge`);
      toast.success(`Nudge sent to ${data.sent_to} — ${data.missing.length} missing document${data.missing.length > 1 ? "s" : ""} listed`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not send the nudge");
    } finally { setNudging(null); }
  };

  const addKpi = async (cid) => {
    const d = kpiDraft[cid] || {};
    if (!d.label || !d.target) return toast.error("KPI needs a name and target");
    await api.post("/studio/kpis", { company_id: cid, label: d.label, target: d.target, current: d.current || "" });
    setKpiDraft((s) => ({ ...s, [cid]: {} })); load();
  };
  const updateKpi = async (kpiId, current) => { await api.put(`/studio/kpis/${kpiId}`, { current }); load(); };
  const delKpi = async (kpiId) => { await api.delete(`/studio/kpis/${kpiId}`); load(); };

  const addIntro = async (cid) => {
    const d = introDraft[cid] || {};
    if (!d.funder_name) return toast.error("Enter the funder's name");
    await api.post("/studio/intros", { company_id: cid, funder_name: d.funder_name, note: d.note || "" });
    setIntroDraft((s) => ({ ...s, [cid]: {} })); load();
  };
  const setIntroStatus = async (introId, status) => { await api.put(`/studio/intros/${introId}`, { status }); load(); };
  const delIntro = async (introId) => { await api.delete(`/studio/intros/${introId}`); load(); };

  return (
    <div className="min-h-screen bg-neutral-950 text-neutral-100" style={{ fontFamily: "Manrope, sans-serif" }}>
      <div className="mx-auto max-w-6xl px-6 py-10">
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-emerald-400">Studio Portal</p>
            <h1 className="mt-1 text-3xl font-extrabold">Portfolio Dashboard</h1>
            <p className="mt-1 text-sm text-neutral-400">{user?.name || user?.email} · monthly progress reports land in your inbox on the 1st</p>
          </div>
          <div className="flex gap-2">
            <button data-testid="studio-closeout-btn" onClick={downloadCloseout} disabled={closeoutBusy}
                    className="inline-flex items-center gap-1.5 rounded-lg bg-amber-500/15 border border-amber-500/40 px-3 py-2 text-xs font-bold text-amber-300 hover:bg-amber-500/25 disabled:opacity-60">
              {closeoutBusy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <FileText className="h-3.5 w-3.5" />} Close-Out Report
            </button>
            <button data-testid="studio-refresh" onClick={load} className="inline-flex items-center gap-1.5 rounded-lg border border-neutral-700 px-3 py-2 text-xs font-bold text-neutral-300 hover:bg-neutral-800"><RefreshCcw className="h-3.5 w-3.5" /> Refresh</button>
            <button data-testid="studio-logout" onClick={logout} className="inline-flex items-center gap-1.5 rounded-lg border border-neutral-700 px-3 py-2 text-xs font-bold text-neutral-300 hover:bg-neutral-800"><LogOut className="h-3.5 w-3.5" /> Log out</button>
          </div>
        </div>

        <form onSubmit={addCompany} className="mt-8 flex flex-col gap-3 rounded-2xl border border-neutral-800 bg-neutral-900 p-5 sm:flex-row">
          <input required type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                 placeholder="Founder's Clinq email — link their company to your portfolio"
                 data-testid="studio-add-email"
                 className="flex-1 rounded-md border border-neutral-700 bg-neutral-950 px-4 py-2.5 text-sm focus:border-emerald-500 focus:outline-none" />
          <button type="submit" disabled={busy} data-testid="studio-add-submit"
                  className="inline-flex items-center justify-center gap-1.5 rounded-md bg-emerald-500 px-5 py-2.5 text-sm font-bold text-neutral-950 hover:bg-emerald-400 disabled:opacity-60">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />} Add company
          </button>
        </form>

        {inviteOffer && (
          <div data-testid="studio-invite-offer" className="mt-3 flex flex-wrap items-center gap-3 rounded-xl border border-emerald-500/30 bg-emerald-500/5 px-4 py-3">
            <Mail className="h-4 w-4 shrink-0 text-emerald-400" />
            <p className="flex-1 min-w-[220px] text-sm text-neutral-300">
              No Clinq account for <b className="text-neutral-100">{inviteOffer}</b> yet — invite them and their company joins your cohort automatically when they sign up.
            </p>
            <div className="flex gap-2">
              <button data-testid="studio-invite-send" onClick={() => sendInvite(inviteOffer)} disabled={busy}
                      className="rounded-md bg-emerald-500 px-4 py-2 text-xs font-bold text-neutral-950 hover:bg-emerald-400 disabled:opacity-60">Send invite</button>
              <button data-testid="studio-invite-dismiss" onClick={() => setInviteOffer(null)}
                      className="rounded-md border border-neutral-700 px-3 py-2 text-xs font-bold text-neutral-400 hover:bg-neutral-800">Dismiss</button>
            </div>
          </div>
        )}

        {invites.filter((i) => i.status === "pending").length > 0 && (
          <div className="mt-3 flex flex-wrap items-center gap-2" data-testid="studio-pending-invites">
            <span className="text-[10px] font-bold uppercase tracking-wider text-neutral-500">Pending invites</span>
            {invites.filter((i) => i.status === "pending").map((i) => (
              <span key={i.invite_id} data-testid={`studio-invite-row-${i.invite_id}`}
                    className="inline-flex items-center gap-1.5 rounded-full border border-neutral-700 bg-neutral-900 px-3 py-1 text-xs text-neutral-300">
                <Mail className="h-3 w-3 text-emerald-400" /> {i.email}
                <button onClick={() => revokeInvite(i.invite_id)} data-testid={`studio-invite-revoke-${i.invite_id}`}
                        className="ml-0.5 text-neutral-600 hover:text-red-400" title="Revoke invite"><X className="h-3 w-3" /></button>
              </span>
            ))}
          </div>
        )}

        {loading ? (
          <div className="mt-16 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-emerald-400" /></div>
        ) : rows.length === 0 ? (
          <p className="mt-10 text-sm text-neutral-500" data-testid="studio-empty">No portfolio companies yet — add your first founder above.</p>
        ) : rows.map((r) => (
          <div key={r.company_id} data-testid={`studio-company-${r.company_id}`} className="mt-6 rounded-2xl border border-neutral-800 bg-neutral-900 p-6">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <p className="text-lg font-extrabold">{r.company_name}</p>
                <p className="text-xs text-neutral-500">{r.founder_email} · {r.sector || "—"} · {r.country || "—"} · {r.kind}</p>
              </div>
              <div className="flex items-center gap-4">
                <div className="text-right">
                  <p className="text-[10px] font-bold uppercase tracking-wider text-neutral-500">CCR Score</p>
                  <p className="text-2xl font-extrabold text-emerald-400">{r.readiness_score}<span className="text-sm text-neutral-500">/100</span></p>
                </div>
                <span className="rounded-full bg-emerald-500/10 px-3 py-1 text-[11px] font-bold text-emerald-300">Stage {r.stage_number}: {r.stage_label}</span>
                {r.has_report && (
                  <button onClick={() => downloadReport(r.company_id, r.company_name)}
                          data-testid={`studio-report-btn-${r.company_id}`} title="Download full CCR report & roadmap"
                          className="inline-flex items-center gap-1.5 rounded-lg border border-teal-500/40 bg-teal-500/10 px-3 py-1.5 text-xs font-bold text-teal-300 hover:bg-teal-500/20">
                    <FileText className="h-3.5 w-3.5" /> CCR Report
                  </button>
                )}
              </div>
            </div>

            <div className="mt-4 flex flex-wrap items-center gap-2" data-testid={`dataroom-${r.company_id}`}>
              <span className="inline-flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider text-neutral-500">
                <FolderOpen className="h-3.5 w-3.5 text-teal-400" /> Data room
              </span>
              {(r.data_room || []).map((d) => (
                <span key={d.key} data-testid={`dataroom-doc-${d.key}-${r.company_id}`}
                      title={d.status === "uploaded" ? "Uploaded" : d.status === "deferred" ? "Deferred — founder flagged as not available yet" : "Missing"}
                      className={"inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-[11px] font-semibold " +
                        (d.status === "uploaded" ? "border-emerald-500/30 bg-emerald-500/10 text-emerald-300"
                          : d.status === "deferred" ? "border-amber-500/30 bg-amber-500/10 text-amber-300"
                          : "border-neutral-700 bg-neutral-800 text-neutral-500")}>
                  {d.status === "uploaded" ? <Check className="h-3 w-3" /> : d.status === "deferred" ? <Clock className="h-3 w-3" /> : <X className="h-3 w-3" />}
                  {d.label}
                </span>
              ))}
              {r.extra_docs > 0 && (
                <span className="text-[11px] text-neutral-500" data-testid={`dataroom-extra-${r.company_id}`}>
                  +{r.extra_docs} more document{r.extra_docs > 1 ? "s" : ""}
                </span>
              )}
              {(r.data_room || []).some((d) => d.status === "missing") && (
                <button onClick={() => nudgeFounder(r.company_id)} disabled={nudging === r.company_id}
                        data-testid={`doc-nudge-btn-${r.company_id}`} title="Email the founder a list of their missing documents"
                        className="ml-auto inline-flex items-center gap-1.5 rounded-md border border-amber-500/40 bg-amber-500/10 px-2.5 py-1 text-[11px] font-bold text-amber-300 hover:bg-amber-500/20 disabled:opacity-60">
                  {nudging === r.company_id ? <Loader2 className="h-3 w-3 animate-spin" /> : <BellRing className="h-3 w-3" />} Nudge founder
                </button>
              )}
            </div>

            {(r.red_flags || []).length > 0 && (
              <div className="mt-3 rounded-xl border border-rose-500/20 bg-rose-500/[0.05] px-4 py-3" data-testid={`red-flags-${r.company_id}`}>
                <span className="inline-flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider text-rose-300">
                  <AlertTriangle className="h-3.5 w-3.5" /> Top red flags before due diligence
                </span>
                <ul className="mt-1.5 space-y-1">
                  {r.red_flags.map((flag, i) => (
                    <li key={i} className="text-[12.5px] leading-snug text-rose-200/80" data-testid={`red-flag-${i}-${r.company_id}`}>• {flag}</li>
                  ))}
                </ul>
              </div>
            )}

            <div className="mt-4 flex flex-wrap gap-2">
              <button onClick={() => togglePanel(r.company_id, "map")} data-testid={`investor-map-btn-${r.company_id}`}
                      className={"inline-flex items-center gap-1.5 rounded-lg border px-3 py-1.5 text-xs font-bold " +
                        (expanded[r.company_id] === "map" ? "border-teal-400 bg-teal-500/20 text-teal-200" : "border-teal-500/40 bg-teal-500/10 text-teal-300 hover:bg-teal-500/20")}>
                <Map className="h-3.5 w-3.5" /> Investor Map
              </button>
              <button onClick={() => togglePanel(r.company_id, "strategy")} data-testid={`strategy-btn-${r.company_id}`}
                      className={"inline-flex items-center gap-1.5 rounded-lg border px-3 py-1.5 text-xs font-bold " +
                        (expanded[r.company_id] === "strategy" ? "border-amber-400 bg-amber-500/20 text-amber-200" : "border-amber-500/40 bg-amber-500/10 text-amber-300 hover:bg-amber-500/20")}>
                <Sparkles className="h-3.5 w-3.5" /> Fundraise Strategy
              </button>
            </div>
            {expanded[r.company_id] === "map" && <InvestorMapPanel companyId={r.company_id} />}
            {expanded[r.company_id] === "strategy" && <StrategyPanel companyId={r.company_id} />}

            <div className="mt-5 grid gap-5 lg:grid-cols-2">
              {/* KPIs */}
              <div className="rounded-xl border border-neutral-800 bg-neutral-950 p-4">
                <p className="inline-flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider text-neutral-500"><Target className="h-3.5 w-3.5 text-amber-400" /> KPI Milestones</p>
                {(r.kpis || []).map((k) => (
                  <div key={k.kpi_id} className="mt-2 flex items-center gap-2 text-sm">
                    <span className="flex-1 truncate">{k.label}</span>
                    <input defaultValue={k.current} onBlur={(e) => e.target.value !== k.current && updateKpi(k.kpi_id, e.target.value)}
                           data-testid={`kpi-current-${k.kpi_id}`}
                           className="w-24 rounded border border-neutral-700 bg-neutral-900 px-2 py-1 text-xs" />
                    <span className="text-xs text-neutral-500">/ {k.target}</span>
                    <button onClick={() => delKpi(k.kpi_id)} className="text-neutral-600 hover:text-red-400"><Trash2 className="h-3.5 w-3.5" /></button>
                  </div>
                ))}
                <div className="mt-3 flex flex-wrap gap-2">
                  <input placeholder="KPI (e.g. MRR)" value={kpiDraft[r.company_id]?.label || ""}
                         onChange={(e) => setKpiDraft((s) => ({ ...s, [r.company_id]: { ...s[r.company_id], label: e.target.value } }))}
                         data-testid={`kpi-label-${r.company_id}`}
                         className="flex-1 min-w-[110px] rounded border border-neutral-700 bg-neutral-900 px-2 py-1.5 text-xs" />
                  <input placeholder="Target" value={kpiDraft[r.company_id]?.target || ""}
                         onChange={(e) => setKpiDraft((s) => ({ ...s, [r.company_id]: { ...s[r.company_id], target: e.target.value } }))}
                         data-testid={`kpi-target-${r.company_id}`}
                         className="w-24 rounded border border-neutral-700 bg-neutral-900 px-2 py-1.5 text-xs" />
                  <button onClick={() => addKpi(r.company_id)} data-testid={`kpi-add-${r.company_id}`}
                          className="rounded bg-amber-500/20 px-3 py-1.5 text-xs font-bold text-amber-300 hover:bg-amber-500/30">Add</button>
                </div>
              </div>
              {/* Intros */}
              <div className="rounded-xl border border-neutral-800 bg-neutral-950 p-4">
                <p className="inline-flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider text-neutral-500"><Handshake className="h-3.5 w-3.5 text-sky-400" /> Warm Introductions</p>
                {(r.intros || []).map((it) => (
                  <div key={it.intro_id} className="mt-2 flex items-center gap-2 text-sm">
                    <span className="flex-1 truncate">{it.funder_name}</span>
                    <select value={it.status} onChange={(e) => setIntroStatus(it.intro_id, e.target.value)}
                            data-testid={`intro-status-${it.intro_id}`}
                            className="rounded border border-neutral-700 bg-neutral-900 px-2 py-1 text-xs">
                      {INTRO_STATUSES.map((s) => <option key={s} value={s}>{STATUS_LABEL[s]}</option>)}
                    </select>
                    <button onClick={() => delIntro(it.intro_id)} data-testid={`intro-delete-${it.intro_id}`}
                            className="text-neutral-600 hover:text-red-400"><Trash2 className="h-3.5 w-3.5" /></button>
                  </div>
                ))}
                <div className="mt-3 flex flex-wrap gap-2">
                  <input placeholder="Funder name" value={introDraft[r.company_id]?.funder_name || ""}
                         onChange={(e) => setIntroDraft((s) => ({ ...s, [r.company_id]: { ...s[r.company_id], funder_name: e.target.value } }))}
                         data-testid={`intro-name-${r.company_id}`}
                         className="flex-1 min-w-[140px] rounded border border-neutral-700 bg-neutral-900 px-2 py-1.5 text-xs" />
                  <button onClick={() => addIntro(r.company_id)} data-testid={`intro-add-${r.company_id}`}
                          className="rounded bg-sky-500/20 px-3 py-1.5 text-xs font-bold text-sky-300 hover:bg-sky-500/30">Log intro</button>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
